import React, { useState, useEffect } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Box,
  makeStyles,
  Fade,
  ClickAwayListener,
  Typography,
  Button,
  Tooltip
} from '@material-ui/core';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  Room as PinIcon,
  VolumeOff as MuteIcon,
  Archive as ArchiveIcon,
  Add as AddIcon
} from '@material-ui/icons';
import ChatSortMenu from '../ChatSortMenu';

const useStyles = makeStyles((theme) => ({
  searchContainer: {
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  // ✅ Novo header com título e botão
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  title: {
    fontSize: '1.2rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  newChatButton: {
    minWidth: 'auto',
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      transform: 'scale(1.05)',
    },
    transition: 'all 0.2s ease',
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 20,
      backgroundColor: theme.palette.background.default,
      '& fieldset': {
        borderColor: 'transparent',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.light,
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 14px',
      fontSize: '0.9rem',
    }
  },
  filtersContainer: {
    display: 'flex',
    gap: 6,
    marginTop: 8,
    flexWrap: 'wrap',
  },
  filterChip: {
    height: 26,
    fontSize: '0.75rem',
    '& .MuiChip-icon': {
      fontSize: '0.875rem',
    },
  },
  activeFilter: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  }
}));

const ChatSearchFilter = ({ 
  onSearchChange, 
  onFilterChange, 
  searchValue = '', 
  activeFilters = [],
  chatCounts = {},
  onSortChange,
  currentSort = 'updatedAt',
  currentSortDirection = 'desc',
  onNewChatClick // ✅ Nova prop para criar novo chat
}) => {
  const classes = useStyles();
  const [localSearch, setLocalSearch] = useState(searchValue);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setLocalSearch(searchValue);
  }, [searchValue]);

  // Debounce da busca
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(localSearch);
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearch, onSearchChange]);

  const handleFilterToggle = (filterType) => {
    const newFilters = activeFilters.includes(filterType)
      ? activeFilters.filter(f => f !== filterType)
      : [...activeFilters, filterType];
    
    onFilterChange(newFilters);
  };

  const handleClearSearch = () => {
    setLocalSearch('');
    onSearchChange('');
  };

  const filters = [
    { 
      type: 'pinned', 
      label: 'Fixados', 
      icon: PinIcon, 
      count: chatCounts.pinned || 0 
    },
    { 
      type: 'muted', 
      label: 'Silenciados', 
      icon: MuteIcon, 
      count: chatCounts.muted || 0 
    },
    { 
      type: 'archived', 
      label: 'Arquivados', 
      icon: ArchiveIcon, 
      count: chatCounts.archived || 0 
    },
    { 
      type: 'unread', 
      label: 'Não lidas', 
      icon: null, 
      count: chatCounts.unread || 0 
    }
  ];
  return (
    <ClickAwayListener onClickAway={() => setShowFilters(false)}>
      <div className={classes.searchContainer}>
        {/* ✅ Header com título e botão de novo chat */}
        <div className={classes.headerContainer}>          <Typography className={classes.title}>
            Conversas
          </Typography>
          {onNewChatClick && (
            <Tooltip title="Novo chat">
              <Button
                className={classes.newChatButton}
                onClick={onNewChatClick}
                variant="contained"
                size="small"
              >
                <AddIcon fontSize="small" />
              </Button>
            </Tooltip>
          )}
        </div>

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar conversas..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {localSearch && (
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}                <IconButton 
                  size="small" 
                  onClick={() => setShowFilters(!showFilters)}
                  color={activeFilters.length > 0 ? 'primary' : 'default'}
                >
                  <FilterIcon fontSize="small" />
                </IconButton>
                {onSortChange && (
                  <ChatSortMenu
                    currentSort={currentSort}
                    currentDirection={currentSortDirection}
                    onSortChange={onSortChange}
                  />
                )}
              </InputAdornment>
            )
          }}        />
        
        {showFilters && (
          <Fade in={showFilters}>
            <Box className={classes.filtersContainer}>
              {filters.map((filter) => {
                const isActive = activeFilters.includes(filter.type);
                const IconComponent = filter.icon;
                
                return (
                  <Chip
                    key={filter.type}
                    label={`${filter.label} (${filter.count})`}
                    icon={IconComponent ? <IconComponent /> : undefined}
                    onClick={() => handleFilterToggle(filter.type)}
                    className={`${classes.filterChip} ${isActive ? classes.activeFilter : ''}`}
                    variant={isActive ? 'default' : 'outlined'}
                    size="small"
                  />
                );
              })}
            </Box>
          </Fade>
        )}
      </div>
    </ClickAwayListener>
  );
};

export default ChatSearchFilter;
